import { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildMatcher,
  classifyFlow,
  fetchLedgerSnapshot,
  fetchRecentFlows,
  XrplSocket,
} from '../lib/xrpl.js';

const MAX_FLOWS = 120;
const SNAPSHOT_INTERVAL = 5 * 60 * 1000;
const RECONNECT_DELAY = 4_000;

export function useXrplLedger(programs, verifiedPrograms) {
  const matcher = useMemo(() => buildMatcher(programs), [programs]);
  const matcherRef = useRef(matcher);
  const clientRef = useRef(null);
  const [state, setState] = useState({
    status: 'connecting',
    endpoint: null,
    ledgerIndex: null,
    supplies: [],
    flows: [],
    snapshotAt: null,
    sessionStartedAt: new Date().toISOString(),
    lastProgramId: null,
    error: null,
  });

  useEffect(() => {
    matcherRef.current = matcher;
  }, [matcher]);

  useEffect(() => {
    let stopped = false;
    let reconnectTimer = null;
    let snapshotTimer = null;
    let heartbeatTimer = null;
    let flowFlushTimer = null;
    let flowQueue = [];
    let reconnectAttempts = 0;

    const mergeFlow = (flow) => {
      flowQueue.push(flow);
      if (flowFlushTimer) return;
      flowFlushTimer = setTimeout(() => {
        flowFlushTimer = null;
        const batch = flowQueue.splice(0).reverse();
        if (!batch.length || stopped) return;
        setState((current) => {
          const seen = new Set(current.flows.map((item) => item.id));
          const additions = batch.filter((item) => {
            if (seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
          });
          if (!additions.length) return current;
          return {
            ...current,
            flows: [...additions, ...current.flows].slice(0, MAX_FLOWS),
            lastProgramId: additions[0].programId,
          };
        });
      }, 240);
    };

    const refreshSnapshot = async (client) => {
      try {
        const snapshot = await fetchLedgerSnapshot(client, verifiedPrograms);
        if (stopped || client !== clientRef.current) return;
        setState((current) => ({
          ...current,
          supplies: snapshot.supplies,
          ledgerIndex: snapshot.ledgerIndex || current.ledgerIndex,
          snapshotAt: new Date().toISOString(),
        }));
      } catch (error) {
        if (!stopped) setState((current) => ({ ...current, error: `Balance refresh: ${error.message}` }));
      }
    };

    const scheduleReconnect = () => {
      if (stopped || reconnectTimer) return;
      reconnectAttempts += 1;
      setState((current) => ({ ...current, status: 'reconnecting' }));
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        start();
      }, Math.min(RECONNECT_DELAY * reconnectAttempts, 20_000));
    };

    const start = async () => {
      if (stopped) return;
      clearInterval(snapshotTimer);
      clearInterval(heartbeatTimer);
      clientRef.current?.close();
      setState((current) => ({ ...current, status: reconnectAttempts ? 'reconnecting' : 'connecting', error: null }));

      const client = new XrplSocket({
        onStream: (message) => {
          if (message.type === 'ledgerClosed') {
            setState((current) => ({
              ...current,
              ledgerIndex: message.ledger_index || current.ledgerIndex,
            }));
            return;
          }
          if (message.type === 'transaction' || message.transaction || message.tx_json) {
            const flow = classifyFlow(message, matcherRef.current, 'live');
            if (flow) mergeFlow(flow);
          }
        },
        onClose: scheduleReconnect,
        onError: () => undefined,
      });
      clientRef.current = client;

      try {
        const endpoint = await client.connect();
        if (stopped || client !== clientRef.current) {
          client.close();
          return;
        }
        const subscription = await client.request('subscribe', { streams: ['ledger', 'transactions'] });
        reconnectAttempts = 0;
        setState((current) => ({
          ...current,
          status: 'live',
          endpoint,
          ledgerIndex: subscription.ledger_index || current.ledgerIndex,
          error: null,
        }));

        refreshSnapshot(client);
        fetchRecentFlows(client, verifiedPrograms, matcherRef.current)
          .then((flows) => {
            if (stopped || client !== clientRef.current) return;
            setState((current) => {
              const existing = new Set(current.flows.map((flow) => flow.id));
              const additions = flows.filter((flow) => !existing.has(flow.id));
              return { ...current, flows: [...current.flows, ...additions].slice(0, MAX_FLOWS) };
            });
          })
          .catch(() => undefined);

        snapshotTimer = setInterval(() => refreshSnapshot(client), SNAPSHOT_INTERVAL);
        heartbeatTimer = setInterval(() => {
          client.request('server_info').catch(() => client.socket?.close());
        }, 30_000);
      } catch (error) {
        if (stopped) return;
        setState((current) => ({ ...current, status: 'offline', error: error.message }));
        scheduleReconnect();
      }
    };

    start();
    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      clearTimeout(flowFlushTimer);
      clearInterval(snapshotTimer);
      clearInterval(heartbeatTimer);
      clientRef.current?.close();
    };
  }, [verifiedPrograms]);

  return state;
}
